import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from '../i18n'
import { useTheme } from '../hooks/useTheme'
import { useUserPreferences } from '../contexts/UserPreferencesContext'
import { userApi } from '../api'
import type { UpdateUserPreferencesRequest } from '../types/api'
import { Button } from './ui'
import { captureEvent } from '../posthog'

const FEATURE_ICONS = [
  // clipboard-list: create & manage training sets
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
    <rect x="9" y="3" width="6" height="4" rx="1"/>
    <line x1="9" y1="12" x2="15" y2="12"/>
    <line x1="9" y1="16" x2="13" y2="16"/>
  </svg>,
  // bar-chart: track progress
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10"/>
    <line x1="12" y1="20" x2="12" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="14"/>
  </svg>,
  // layout: templates
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <path d="M3 9h18"/>
    <path d="M9 21V9"/>
  </svg>,
  // bell: notifications
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>,
  // trending-up: statistics
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
    <polyline points="17 6 23 6 23 12"/>
  </svg>,
  // users: manage clients
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>,
]

const FEATURE_COLORS = ['#0088cc', '#0ea5e9', '#f59e0b', '#10b981', '#3b82f6', '#14b8a6']

export default function BecomeSpecialistFlow() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const theme = useTheme()
  const { refreshPreferences } = useUserPreferences()

  const [saving, setSaving] = useState(false)

  const handleContinue = async () => {
    setSaving(true)
    try {
      const request: UpdateUserPreferencesRequest = {}
      await userApi.updateUserPreferences(request)
      await refreshPreferences()
      captureEvent('become_specialist_started')
    } catch (error) {
      console.error('Error saving default preferences:', error)
    } finally {
      setSaving(false)
    }
    navigate('/invite', {
      state: { fromBecomeSpecialist: true },
    })
  }

  const features = t.becomeSpecialist.abilities.features as string[]

  return (
    <div className="p-5">
      <div className="flex flex-col items-center mb-8 pt-2">
        <div
          className="w-20 h-20 rounded-3xl flex items-center justify-center mb-5"
          style={{ backgroundColor: `${theme.buttonColor}18` }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: theme.buttonColor }}>
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
            <line x1="12" y1="22.08" x2="12" y2="12"/>
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-center mb-2" style={{ color: theme.text.primary }}>
          {t.becomeSpecialist.abilities.title}
        </h1>
        <p className="text-sm text-center px-6" style={{ color: theme.text.muted }}>
          {t.becomeSpecialist.abilities.subtitle}
        </p>
      </div>

      <div className="flex flex-col gap-2.5 mb-8">
        {features.map((feature, index) => (
          <div
            key={index}
            className="flex items-center gap-3 px-4 py-3.5 rounded-2xl"
            style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}` }}
          >
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{
                backgroundColor: `${FEATURE_COLORS[index % FEATURE_COLORS.length]}18`,
                color: FEATURE_COLORS[index % FEATURE_COLORS.length],
              }}
            >
              {FEATURE_ICONS[index % FEATURE_ICONS.length]}
            </div>
            <p className="text-sm font-medium flex-1" style={{ color: theme.text.primary }}>
              {feature}
            </p>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: theme.buttonColor, flexShrink: 0 }}>
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
        ))}
      </div>

      <Button
        onClick={handleContinue}
        variant="primary"
        size="lg"
        fullWidth
        disabled={saving}
      >
        {saving ? t.common.loading : t.becomeSpecialist.abilities.continue}
      </Button>
    </div>
  )
}
